import mustache from "mustache";
import { BaseRequest } from "../../../../base/request/BaseRequest";
import { StorageService } from "../../../../comun/services/storage/StorageService";
import { FormField } from "../../../base/form/FormField";
import { IInputFileField } from "../../../definitions/form/inputs/IInputFileField";
import { FileConfig } from "../../../definitions/form/inputs/config/FileConfig";
import { FileValidation } from "../../../definitions/form/inputs/validation/FileValidation";
import template from "./template.html";

mustache.parse(template);

/** Campo de archivo del sistema nuevo. Al seleccionar un archivo lo sube de
 *  inmediato al storage (`GET storage/sas-url` + PUT del blob) y escribe la URL
 *  resultante en el request; mientras sube, el campo es inválido ("Espere…").
 *  El botón limpiar borra la URL (`null`, nunca `""`). */
export class InputFileField<TRequest extends BaseRequest>
  extends FormField<FileConfig<TRequest>>
  implements IInputFileField
{
  private readonly _request: TRequest;
  private readonly _validation: FileValidation;
  private readonly _storage = new StorageService();

  constructor(
    request: TRequest,
    config: FileConfig<TRequest>,
    validation: FileValidation,
  ) {
    super(config);
    this._request = request;
    this._validation = validation;
  }

  private _onValueChange?: () => void;

  setOnChange(handler: () => void): void {
    this._onValueChange = handler;
  }

  /** URL del archivo subido (desde el request). */
  getValue(): string | null {
    return this._config.get(this._request);
  }

  getTemplate(): string {
    return template;
  }

  getData(): unknown {
    const isRequired = this._validation.isRequired ?? false;
    return {
      label: `${this._config.label}${isRequired ? " *" : ""}`,
      description: this._config.description ?? "",
      accept: this._config.accept ?? "",
      btnLabel: this._config.btnLabel ?? "Elegir archivo",
      emptyText: this._config.emptyText ?? "No se eligió ningún archivo",
      id: this._formFieldId,
    };
  }

  connect(): void {
    this._input = document.getElementById(
      `input-${this._formFieldId}`,
    ) as HTMLInputElement | null;
    this._btn = document.getElementById(`file-btn-${this._formFieldId}`);
    this._name = document.getElementById(`file-name-${this._formFieldId}`);
    this._clearBtn = document.getElementById(
      `file-clear-${this._formFieldId}`,
    ) as HTMLButtonElement | null;
    this._error = document.getElementById(`error-${this._formFieldId}`);

    this._btn?.addEventListener("click", this._onPick);
    this._clearBtn?.addEventListener("click", this._onClear);
    this._input?.addEventListener("change", this._onFileChange);
  }

  doDispose(): void {
    this._btn?.removeEventListener("click", this._onPick);
    this._clearBtn?.removeEventListener("click", this._onClear);
    this._input?.removeEventListener("change", this._onFileChange);
  }

  getError(): string | null {
    if (this._isUploading) return "Espere a que termine la subida del archivo";
    if (this._uploadFailed) return "Error al subir el archivo; reintente";
    const isRequired = this._validation.isRequired ?? false;
    if (isRequired && !this._config.get(this._request)) {
      return `Seleccione ${this._config.label}`;
    }
    return null;
  }

  showError(): void {
    if (this._error) this._error.innerHTML = this.getError() ?? "";
  }

  focus(): void {
    this._btn?.focus();
  }

  disabled(): void {
    if (this._input) this._input.disabled = true;
    if (this._btn) this._btn.style.pointerEvents = "none";
    if (this._clearBtn) this._clearBtn.disabled = true;
  }

  enabled(): void {
    if (this._input) this._input.disabled = false;
    if (this._btn) this._btn.style.pointerEvents = "";
    if (this._clearBtn) this._clearBtn.disabled = false;
  }

  private _input: HTMLInputElement | null = null;
  private _btn: HTMLElement | null = null;
  private _name: HTMLElement | null = null;
  private _clearBtn: HTMLButtonElement | null = null;
  private _error: HTMLElement | null = null;
  /** Subida en curso (el campo es inválido mientras tanto). */
  private _isUploading = false;
  /** La última subida falló (reintentar seleccionando de nuevo). */
  private _uploadFailed = false;

  /** Abre el diálogo de selección de archivo. */
  private _onPick = (): void => {
    this._input?.click();
  };

  /** Limpia el archivo: borra la URL del request (null) y resetea la UI. */
  private _onClear = (): void => {
    if (this._input) this._input.value = "";
    this._config.set(this._request, null);
    this._uploadFailed = false;
    this._setFileName(null);
    this.showError();
    this._onValueChange?.();
  };

  /** Al seleccionar archivo: lo sube al storage y persiste la URL resultante. */
  private _onFileChange = (): void => {
    const file = this._input?.files?.[0];
    if (!file) return;
    void this._uploadAsync(file);
  };

  /** Sube el archivo mostrando el estado; al éxito escribe la URL en el request. */
  private async _uploadAsync(file: File): Promise<void> {
    this._isUploading = true;
    this._uploadFailed = false;
    this._config.set(this._request, null);
    if (this._name) this._name.textContent = `Subiendo ${file.name}…`;
    this.showError();

    try {
      const url = await this._storage.upload(file);
      if (this._disposed) return;
      this._config.set(this._request, url);
      this._setFileName(file.name);
    } catch {
      if (this._disposed) return;
      this._uploadFailed = true;
      if (this._input) this._input.value = "";
      this._setFileName(null);
    } finally {
      if (!this._disposed) {
        this._isUploading = false;
        this.showError();
        this._onValueChange?.();
      }
    }
  }

  /** Muestra el nombre del archivo subido (o el texto vacío) y el botón limpiar. */
  private _setFileName(fileName: string | null): void {
    if (this._name) {
      this._name.textContent =
        fileName ?? this._config.emptyText ?? "No se eligió ningún archivo";
    }
    if (this._clearBtn) {
      this._clearBtn.style.display = fileName ? "" : "none";
    }
  }
}
